import { Link, Route, Switch, useLocation, Redirect } from "wouter";
import { useEffect, useState } from "react";
import { AikoBubble } from "../aiko/AikoBubble";
import { useOutletId, setOutletId, ALL_OUTLETS_SENTINEL } from "../lib/outlet";
import { Dashboard } from "./pages/Dashboard";
import { Orders } from "./pages/Orders";
import { Suppliers } from "./pages/Suppliers";
import { Invoices } from "./pages/Invoices";
import { Settings } from "./pages/Settings";
import { Reports } from "./pages/Reports";
import { Outlets } from "./pages/Outlets";
import { NotFound404 } from "./pages/NotFound404";
import { GenerativePage } from "./pages/GenerativePage";

/**
 * AppShell mirrors the buyer.zeemart.co IA:
 *   - Dark topbar with brand at left, user at right
 *   - Light sidebar with vertical nav matching the original order
 *   - Main content area renders the active route
 *   - Floating Aiko bubble (bottom-right) opens a companion drawer
 *
 * Routes follow the original paths so muscle memory survives.
 */

type NavItem = { href: string; label: string; icon: string };

const NAV: ReadonlyArray<NavItem> = [
  { href: "/app/dashboard",  label: "Dashboard", icon: "📊" },
  { href: "/app/reports",    label: "Reports",   icon: "📈" },
  { href: "/app/orders",     label: "Orders",    icon: "📦" },
  { href: "/app/invoices",   label: "Invoices",  icon: "🧾" },
  { href: "/app/payments",   label: "Payments",  icon: "💳" },
  { href: "/app/suppliers",  label: "Suppliers", icon: "🏷️" },
  { href: "/app/inventory",  label: "Inventory", icon: "📋" },
  { href: "/app/outlets",    label: "Outlets",   icon: "🏪" },
  { href: "/app/users",      label: "Users",     icon: "👥" },
  { href: "/app/news",       label: "News",      icon: "📰" },
];

export function AppShell() {
  return (
    <div className="min-h-[100dvh] grid grid-rows-[3.5rem_1fr] bg-app-bg">
      <TopBar />
      <div className="grid grid-cols-1 md:grid-cols-[14.5rem_1fr] min-h-0">
        <SideBar />
        <main className="min-w-0 px-6 md:px-10 py-7 overflow-y-auto">
          <Switch>
            <Route path="/app" component={() => <Redirect to="/app/dashboard" />} />
            <Route path="/app/dashboard"  component={Dashboard} />
            <Route path="/app/reports"    component={Reports} />
            <Route path="/app/orders"     component={Orders} />
            <Route path="/app/orders/:bucket" component={Orders} />
            <Route path="/app/invoices"   component={Invoices} />
            <Route path="/app/invoices/:bucket" component={Invoices} />
            <Route path="/app/payments"   component={GenerativePage} />
            <Route path="/app/suppliers"  component={Suppliers} />
            <Route path="/app/inventory"  component={GenerativePage} />
            <Route path="/app/outlets"    component={Outlets} />
            <Route path="/app/users"      component={GenerativePage} />
            <Route path="/app/news"       component={GenerativePage} />
            <Route path="/app/settings"   component={Settings} />
            <Route path="/app/settings/:tab" component={Settings} />
            <Route path="/app/:rest*" component={NotFound404} />
          </Switch>
        </main>
      </div>
      <AikoBubble />
    </div>
  );
}

type SessOutlet = { outletId: string; outletName: string };
type SessShape = {
  ok?: boolean;
  user?: { firstName?: string; lastName?: string };
  outlets?: SessOutlet[];
};

function TopBar() {
  const [sess, setSess] = useState<SessShape | null>(null);
  useEffect(() => {
    void fetch("/api/auth/session")
      .then((r) => r.json())
      .then((d) => {
        if (d?.ok) setSess(d as SessShape);
      })
      .catch(() => {});
  }, []);
  const name = sess?.user
    ? `${sess.user.firstName ?? ""} ${sess.user.lastName ?? ""}`.trim() || "Buyer"
    : "Sign in";

  return (
    <header
      role="topbar"
      className="bg-topbar-bg text-topbar-ink px-6 flex items-center justify-between h-14 sticky top-0 z-30"
    >
      <Link href="/app/dashboard" className="flex items-center gap-2 group">
        <span aria-hidden className="inline-flex w-7 h-7 rounded-full bg-nomni-green/20 items-center justify-center">
          <span className="w-3.5 h-3.5 rounded-full bg-nomni-green block" />
        </span>
        <span className="text-lg tracking-tight">
          <span className="font-medium">nomni</span>{" "}
          <span className="text-nomni-green font-medium">Procure</span>
        </span>
      </Link>
      <div className="flex items-center gap-3 text-sm">
        <OutletPicker outlets={sess?.outlets ?? []} />
        <span className="opacity-80">{name}</span>
        <a
          href="/api/auth/logout"
          className="opacity-70 hover:opacity-100 transition text-xs"
          title="Sign out"
        >
          ↩
        </a>
      </div>
    </header>
  );
}

function OutletPicker({ outlets }: { outlets: ReadonlyArray<SessOutlet> }) {
  // Default is "all-outlets" — matches original buyer.zeemart.co aggregate behaviour
  const current = useOutletId(ALL_OUTLETS_SENTINEL);
  if (outlets.length === 0) return null;
  return (
    <label className="hidden md:inline-flex items-center gap-2 text-xs opacity-80">
      <span className="uppercase tracking-wider">Outlet</span>
      <select
        value={current ?? ALL_OUTLETS_SENTINEL}
        onChange={(e) => setOutletId(e.target.value)}
        className="bg-topbar-bg/0 border border-topbar-ink/20 rounded px-2 py-1 text-topbar-ink text-sm focus:outline-none focus:border-nomni-green"
      >
        <option value={ALL_OUTLETS_SENTINEL} className="text-ink bg-canvas-2">
          All outlets ({outlets.length})
        </option>
        {outlets.map((o) => (
          <option key={o.outletId} value={o.outletId} className="text-ink bg-canvas-2">
            {o.outletName} · {o.outletId}
          </option>
        ))}
      </select>
    </label>
  );
}

function SideBar() {
  const [loc] = useLocation();
  return (
    <aside
      role="sidebar"
      className="hidden md:block bg-sidebar-bg border-r border-line py-4"
    >
      <nav className="space-y-1">
        {NAV.map((it) => {
          const active = loc === it.href || loc.startsWith(it.href + "/");
          return (
            <Link
              key={it.href}
              href={it.href}
              className={
                "flex items-center gap-3 mx-2 px-3 py-2.5 rounded-md text-sm transition " +
                (active
                  ? "bg-accent-active text-spinach font-medium"
                  : "text-ink-2 hover:bg-line/50")
              }
            >
              <span aria-hidden className="w-5 inline-flex justify-center">
                {it.icon}
              </span>
              {it.label}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
